import { motion } from "framer-motion";
import { CalendarCheck, Clock, MapPin, Phone, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionTag from "@/components/SectionTag";
import { BookButton, CallButton, EmergencyNote } from "@/components/CTA";
import { SITE } from "@/data/site";

export default function Insurance() {
  return (
    <main data-testid="insurance-page">
      <Seo
        title="Insurance & New Patients | Advantage Primary Health, Prescott Valley, AZ"
        description="Advantage Primary Health is now accepting new patients in Prescott Valley, AZ. We accept AHCCCS, UnitedHealthcare, Aetna, Imperial Health, Medicare, Cigna, AZ Complete Health, Ambetter, Wellcare, and self-pay."
      />

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-14 sm:px-6 md:pt-20 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <SectionTag number="01" label="Getting Started" />
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl lg:text-6xl">
              Insurance &amp;{" "}
              <span className="font-serif italic font-medium text-brand-red">
                New Patients
              </span>
            </h1>
            <p className="mt-7 text-base leading-relaxed text-slate-600 sm:text-lg">
              We are now accepting new patients from Prescott Valley, Prescott,
              Chino Valley, and Dewey-Humboldt. Becoming a patient is
              simple—here is everything you need to know.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-slate-50/70">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-12">
          <Reveal>
            <SectionTag number="02" label="Coverage" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl">
              Insurance we accept
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SITE.insurance.map((plan, i) => (
              <Reveal key={plan} delay={i * 0.05}>
                <div
                  data-testid={`insurance-plan-${plan.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`flex items-center gap-3 rounded-2xl border p-5 transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-soft ${
                    plan === "Self-Pay"
                      ? "border-brand-red/30 bg-brand-redlight"
                      : "border-slate-100 bg-white"
                  }`}
                >
                  <ShieldCheck className={`h-5 w-5 shrink-0 ${plan === "Self-Pay" ? "text-brand-red" : "text-slate-400"}`} />
                  <span className="text-sm font-semibold text-brand-navy">
                    {plan === "Self-Pay" ? "Self-Pay Patients Welcome" : plan}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <p data-testid="insurance-coverage-note" className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-500">
              Coverage can vary by plan. Please call our office with questions
              before your appointment.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <div
              data-testid="out-of-network-section"
              className="mt-8 rounded-3xl border border-slate-200 bg-white p-8"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">
                Out-of-Network
              </p>
              <h3 className="mt-3 font-display text-lg font-semibold text-brand-navy">
                Out-of-Network Insurance
              </h3>
              <div className="mt-4 flex flex-wrap gap-3">
                {SITE.outOfNetwork.map((plan) => (
                  <span
                    key={plan}
                    data-testid={`out-of-network-plan-${plan.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="inline-flex items-center rounded-full border border-dashed border-slate-300 bg-slate-50 px-5 py-2.5 text-sm font-medium text-slate-600"
                  >
                    {plan}
                  </span>
                ))}
              </div>
              <p
                data-testid="out-of-network-disclaimer"
                className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-500"
              >
                {SITE.outOfNetworkDisclaimer}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-12">
          <Reveal>
            <SectionTag number="03" label="How to Book" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl">
              Two simple ways to{" "}
              <span className="font-serif italic text-brand-red">be seen</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-3xl border border-slate-100 bg-slate-50/60 p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-redlight">
                  <CalendarCheck className="h-5 w-5 text-brand-red" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-brand-navy">
                  Standard appointments
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-500">
                  Use Book Online anytime to choose an appointment that fits
                  your schedule. It opens our secure scheduling page in a new
                  tab.
                </p>
                <div className="mt-6">
                  <BookButton testid="insurance-book-online-btn" />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl border border-slate-100 bg-slate-50/60 p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-redlight">
                  <Phone className="h-5 w-5 text-brand-red" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-brand-navy">
                  Same-day care
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-500">
                  Need to be seen today? Call our office and we will do our
                  best to fit you in the same day.
                </p>
                <div className="mt-6">
                  <CallButton testid="insurance-call-btn" label={`Call ${SITE.phone}`} />
                </div>
                <EmergencyNote testid="insurance-emergency-note" className="mt-5" />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="mt-10 grid gap-6 rounded-3xl bg-brand-navy p-8 sm:p-10 md:grid-cols-3">
              <div className="flex gap-3.5" data-testid="insurance-address-block">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Address</p>
                  <p className="mt-1.5 text-sm font-medium text-white">{SITE.address}</p>
                </div>
              </div>
              <div className="flex gap-3.5" data-testid="insurance-hours-block">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Hours</p>
                  <p className="mt-1.5 text-sm font-medium text-white">{SITE.hours}</p>
                  <p className="text-xs text-slate-400">{SITE.hoursNote}</p>
                </div>
              </div>
              <div className="flex gap-3.5" data-testid="insurance-phone-block">
                <Phone className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Phone</p>
                  <a href={SITE.phoneHref} data-testid="insurance-phone-link" className="mt-1.5 block text-sm font-medium text-white hover:text-brand-red">
                    {SITE.phone}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
